import { z } from 'zod';

/** Separate accounting model; never an OEM, dispatch, transient or EO result. */
export const GRID_LED_STANDBY_PLANNING_MODEL_VERSION = 'grid-led-standby-planning-v1' as const;
export const GRID_LED_STANDBY_PLANNING_LIMITATIONS = [
  'User-declared planning inputs; document review is not automatic extraction adoption or independent verification.',
  'Static capacity arithmetic does not certify start-up, switching, protection, common-mode independence or probability of serving load.',
  'UPS duration is ideal usable-energy divided by load; conversion efficiency, reserve and transient response are not qualified.',
  'Standby testing is separate load-bank energy and does not displace normal grid imports. Emergency outage operation remains unknown unless explicitly declared.',
  'Fuel-use and CO2e factors are declared accounting factors, not HHV, OEM performance, NOx/CO factors or permit compliance.',
  'Fire-water, gas deliverability, installed layout, acoustics, approvals and simulation execution are outside this assessment.',
] as const;

const sha = z.string().regex(/^[a-f\d]{64}$/);
const id = z.string().min(1).max(160).regex(/^[A-Za-z0-9_.:-]+$/);
const positive = z.number().finite().positive().max(1_000_000_000).nullable();
const nonnegative = z.number().finite().nonnegative().max(1_000_000_000).nullable();
const pct = z.number().finite().min(0).max(100).nullable();
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(value => {
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}, 'Invalid calendar date').nullable();

export const gridLedPlanningDocumentQuoteSchema = z.object({
  documentId: id,
  originalFileSha256: sha,
  bodySha256: sha,
  extractionVersion: z.string().min(1).max(120),
  quote: z.string().min(1).max(4_000),
  /** UTF-16 offsets into the complete stored extracted-source body. */
  start: z.number().int().nonnegative(),
  end: z.number().int().positive(),
}).strict().refine(value => value.end > value.start && value.end - value.start === value.quote.length,
  'Quote offsets must cover the exact literal quotation');

export const gridLedPlanningFieldSourceSchema = z.discriminatedUnion('kind', [
  z.object({
    field: z.string().min(1).max(160), value: z.union([z.number().finite(), z.string()]),
    kind: z.literal('reviewed-document-planning'), reviewed: z.literal(true),
    source: gridLedPlanningDocumentQuoteSchema,
    /** Exact quantity/date expression within the quote. Unsupported mappings
     * must be declared as operator interpretations, not document facts. */
    valueExcerpt: z.string().min(1).max(500),
  }).strict(),
  z.object({
    field: z.string().min(1).max(160), value: z.union([z.number().finite(), z.string()]),
    kind: z.literal('operator-planning-assumption'), reviewed: z.literal(true),
    reason: z.string().min(1).max(1_000),
    /** Literal source context, without attributing the chosen value to it. */
    contextSources: z.array(gridLedPlanningDocumentQuoteSchema).max(5).optional(),
  }).strict(),
]);

export const gridLedStandbyPlanningInputSchema = z.object({
  schemaVersion: z.literal(1),
  authority: z.literal('user-declared-planning'),
  ownerUid: id,
  siteId: id,
  studyAreaSelectionHash: sha,
  demonstrationScenarioId: id.optional(),
  phases: z.array(z.object({
    phaseId: id,
    facilityMW: positive,
    itMW: positive,
    gridImportMW: nonnegative,
    gridServiceDate: date,
    operationTargetDate: date,
  }).strict()).min(1).max(20),
  standby: z.object({
    role: z.literal('emergency-standby-and-testing-only'),
    ratingBasis: z.literal('site-net'),
    unitCount: z.number().int().min(1).max(1_000).nullable(),
    siteNetMWPerUnit: positive,
    /** Individual unavailable units, not loss of the entire count as one group. */
    unavailableUnits: z.number().int().min(0).max(1_000).nullable(),
  }).strict(),
  ups: z.object({ dischargeMW: nonnegative, usableEnergyMWh: nonnegative }).strict(),
  annual: z.object({
    itDesignMW: positive,
    itUtilizationPct: pct,
    pue: z.number().finite().min(1).max(10).nullable(),
    hoursPerYear: z.number().finite().positive().max(8_784).nullable(),
    comparisonPue: z.number().finite().min(1).max(10).nullable(),
    gridTonnesCo2ePerMWh: nonnegative,
    testLoadPct: pct,
    testHoursPerYear: z.number().finite().min(0).max(8_784).nullable(),
    testDuty: z.literal('load-bank-no-grid-displacement'),
    litresPerMWh: nonnegative,
    kgCo2ePerLitre: nonnegative,
    /** Null is a real unknown; testing does not stand in for emergency duty. */
    emergencyOutageEnergyMWh: nonnegative,
  }).strict(),
  sources: z.array(gridLedPlanningFieldSourceSchema).max(300),
}).strict().superRefine((input, ctx) => {
  const phases = new Set<string>();
  input.phases.forEach((phase, index) => {
    if (phases.has(phase.phaseId)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['phases', index, 'phaseId'], message: 'Duplicate phase ID' });
    phases.add(phase.phaseId);
  });
  if (input.standby.unitCount != null && input.standby.unavailableUnits != null && input.standby.unavailableUnits > input.standby.unitCount) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['standby', 'unavailableUnits'], message: 'Unavailable units exceed installed units' });
  }
  const required = gridLedPlanningDeclaredFields(input);
  const seen = new Set<string>();
  for (const source of input.sources) {
    if (seen.has(source.field) || !required.has(source.field) || required.get(source.field) !== source.value) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sources'], message: `Duplicate, stale or unrelated field source: ${source.field}` });
    }
    seen.add(source.field);
  }
  for (const field of required.keys()) if (!seen.has(field)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sources'], message: `Review a source or explicit planning assumption for ${field}` });
  }
});

/** Only asserted values need review; null never receives an invented source. */
export function gridLedPlanningDeclaredFields(input: {
  phases: unknown; standby: unknown; ups: unknown; annual: unknown;
}): Map<string, number | string> {
  const fields = new Map<string, number | string>();
  const visit = (value: unknown, path: string): void => {
    if (value == null) return;
    if (typeof value === 'number' || typeof value === 'string') { fields.set(path, value); return; }
    if (Array.isArray(value)) { value.forEach((child, index) => visit(child, `${path}.${index}`)); return; }
    if (typeof value === 'object') for (const [key, child] of Object.entries(value)) {
      // Identifiers and fixed mode discriminants are validated structurally.
      if (!['phaseId', 'role', 'ratingBasis', 'testDuty'].includes(key)) visit(child, `${path}.${key}`);
    }
  };
  for (const field of ['phases', 'standby', 'ups', 'annual'] as const) visit(input[field], field);
  return fields;
}

export type GridLedStandbyPlanningInput = z.infer<typeof gridLedStandbyPlanningInputSchema>;
export type GridLedPlanningFieldSource = z.infer<typeof gridLedPlanningFieldSourceSchema>;
export type GridLedPlanningDocumentQuote = z.infer<typeof gridLedPlanningDocumentQuoteSchema>;
export type PlanningCheck = 'pass' | 'fail' | 'indeterminate';

/** Private applicability record; the unchanged canonical intent is frozen
 * separately on the report. This never represents a qualified firm rating. */
export interface InactiveGenerationImpactAuthority {
  schemaVersion: 1;
  selectedAssessmentKind: 'grid-led-standby-planning';
  selectedAssessmentId: string;
  selectedInputHash: string;
  selectedResultHash: string;
  selectedAt: string;
  firmCapacityQualification: 'indeterminate';
  engineeringQualification: 'indeterminate';
  inactivePriorV1?: { scenarioId: string; inputHash?: string; resultHash?: string; promotedAt?: string };
  inactiveAppliedPhasePowerPlan: boolean;
  inactiveMaxPowerStudy: boolean;
}

/** Public-safe arithmetic: no private document IDs, paths, quote text or owner. */
export interface GridLedStandbyPlanningResult {
  modelVersion: typeof GRID_LED_STANDBY_PLANNING_MODEL_VERSION;
  authority: 'user-declared-planning';
  staticCapacityVerdict: PlanningCheck;
  engineeringQualification: 'indeterminate';
  installedStandbyMW: number | null;
  unavailableStandbyMW: number | null;
  remainingStandbyMW: number | null;
  phases: Array<{
    phaseId: string;
    facilityMW: number | null;
    itMW: number | null;
    gridImportMW: number | null;
    gridServiceDate: string | null;
    operationTargetDate: string | null;
    gridHeadroomMW: number | null;
    gridCapacityVerdict: PlanningCheck;
    gridScheduleVerdict: PlanningCheck;
    standbyHeadroomMW: number | null;
    standbyCapacityVerdict: PlanningCheck;
    idealUpsMinutes: number | null;
    upsPowerVerdict: PlanningCheck;
    upsEnergyBasisVerdict: PlanningCheck;
  }>;
  annual: {
    itMWh: number | null;
    /** Normal demand/grid-equivalent reference before any emergency displacement. */
    gridMWh: number | null;
    comparisonGridMWh: number | null;
    /** Actual modeled imports are unknown until emergency duty is declared. */
    gridImportsAfterEmergencyMWh: number | null;
    emergencyEnergyBalanceVerdict: PlanningCheck;
    standbyTestMWh: number | null;
    standbyTestLitres: number | null;
    gridTonnesCo2e: number | null;
    gridImportsAfterEmergencyTonnesCo2e: number | null;
    standbyTestTonnesCo2e: number | null;
    /** Normal grid + testing subtotal, not a complete inventory if outage duty is unknown. */
    normalGridAndTestingTonnesCo2e: number | null;
    emergencyOutageEnergyMWh: number | null;
    emergencyOutageLitres: number | null;
    emergencyOutageTonnesCo2e: number | null;
    totalIncludingDeclaredEmergencyTonnesCo2e: number | null;
    gridDisplacementFromTestingMWh: 0;
  };
  gaps: string[];
  limitations: string[];
}

/** Immutable saved revision. Hashes cover all private source quotes and the
 * actual recomputed result; no model output is accepted from the client. */
export interface GridLedStandbyPlanningSnapshot {
  schemaVersion: 1;
  id: string;
  name: string;
  siteId: string;
  ownerUid: string;
  input: GridLedStandbyPlanningInput;
  result: GridLedStandbyPlanningResult;
  inputHash: string;
  resultHash: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  promotedAt?: string;
}

/** One explicit active assessment for capacity/accounting consumers.
 * Selecting planning leaves old v1 snapshots readable but not co-authoritative. */
export interface GenerationAssessmentSelection {
  schemaVersion: 1;
  kind: 'grid-led-standby-planning' | 'generation-impact-v1';
  assessmentId: string;
  inputHash: string;
  resultHash: string;
  selectedAt: string;
}

export interface GridLedPlanningSiteRequest { siteId: string; ownerUid: string }
export interface GridLedPlanningSaveRequest extends GridLedPlanningSiteRequest {
  name: string;
  input: GridLedStandbyPlanningInput;
  assessmentId?: string;
  expectedUpdatedAt?: string;
}
export interface GridLedPlanningPromoteRequest extends GridLedPlanningSiteRequest {
  assessmentId: string;
  expectedUpdatedAt: string;
  expectedInputHash: string;
  expectedResultHash: string;
}
